import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './WaitlistLandingPage.css';

// ── Types ───────────────────────────────────────────────────────────────────

interface CountryDto { id: number; name: string; code: string; }
interface CityDto    { id: number; name: string; country_id: number; }

interface FormState {
  name: string; email: string;
  countryId: string; cityId: string; cityName: string;
  ambassador: boolean;
}
interface FormErrors {
  name?: string; email?: string; country?: string; city?: string;
}

// ── Static fallback dataset ─────────────────────────────────────────────────

const FALLBACK: Record<string, string[]> = {
  'Türkiye':['İstanbul','Ankara','İzmir','Bursa','Antalya','Adana','Konya'],
  'United States':['New York','Los Angeles','Chicago','San Francisco','Boston','Seattle','Austin','Miami'],
  'United Kingdom':['London','Manchester','Birmingham','Edinburgh','Glasgow','Bristol'],
  'Germany':['Berlin','Munich','Hamburg','Frankfurt','Cologne','Stuttgart'],
  'France':['Paris','Lyon','Marseille','Toulouse','Nice','Bordeaux'],
  'Spain':['Madrid','Barcelona','Valencia','Seville','Bilbao','Málaga'],
  'Italy':['Rome','Milan','Naples','Turin','Florence','Bologna'],
  'Netherlands':['Amsterdam','Rotterdam','The Hague','Utrecht'],
  'Japan':['Tokyo','Osaka','Kyoto','Yokohama','Nagoya','Fukuoka'],
  'South Korea':['Seoul','Busan','Incheon','Daegu'],
  'Brazil':['São Paulo','Rio de Janeiro','Brasília','Salvador'],
  'Canada':['Toronto','Vancouver','Montreal','Calgary'],
  'Australia':['Sydney','Melbourne','Brisbane','Perth'],
  'Portugal':['Lisbon','Porto','Braga','Coimbra'],
  'Sweden':['Stockholm','Gothenburg','Malmö','Uppsala'],
};

// ── Phone screen renderers ──────────────────────────────────────────────────

function ExploreScreen() {
  return (
    <div style={{padding:'14px 12px 80px',background:'#F8FAFC',height:'100%',fontFamily:'Inter,sans-serif',overflowY:'auto',boxSizing:'border-box'}}>
      <div style={{fontSize:18,fontWeight:900,letterSpacing:'-0.02em',color:'#0F172A',marginBottom:12}}>Explore</div>
      {[
        {color:'#EF4444',lang:'EN',title:'Morning Coffee Chat',host:'Daniel K.',pax:'3/8',live:true},
        {color:'#6366F1',lang:'ES',title:'Spanish Intermediate',host:'Sofia R.',pax:'5/8',live:false,time:'19:00'},
        {color:'#0EA5E9',lang:'TR',title:'Türkçe Konuşma',host:'Emre A.',pax:'4/6',live:false,time:'20:00'},
      ].map((e,i)=>(
        <div key={i} style={{background:'white',borderRadius:14,padding:11,marginBottom:8,boxShadow:'0 2px 8px rgba(15,23,42,0.07)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
            <span style={{fontSize:9,fontWeight:800,color:e.color,letterSpacing:'0.05em'}}>{e.lang}</span>
            {e.live
              ? <span style={{fontSize:8,fontWeight:800,color:'#EF4444',background:'rgba(239,68,68,0.08)',padding:'2px 6px',borderRadius:999}}>● LIVE</span>
              : <span style={{fontSize:8,color:'#94A3B8'}}>{e.time}</span>}
          </div>
          <div style={{fontSize:12,fontWeight:700,color:'#0F172A',marginBottom:2}}>{e.title}</div>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#64748B',marginTop:6,paddingTop:6,borderTop:'1px solid #F1F5F9'}}>
            <span>by {e.host}</span><span>👥 {e.pax}</span>
          </div>
        </div>
      ))}
      <div style={{position:'absolute',bottom:0,left:0,right:0,height:64,background:'rgba(255,255,255,0.92)',backdropFilter:'blur(12px)',borderTop:'1px solid #F1F5F9',display:'flex',justifyContent:'space-around',alignItems:'center',padding:'0 8px'}}>
        {[{i:'🔍',l:'Explore',a:true},{i:'💬',l:'Msg',a:false},{i:'👥',l:'Groups',a:false},{i:'👤',l:'Me',a:false}].map(t=>(
          <div key={t.l} style={{textAlign:'center'}}>
            <div style={{fontSize:18}}>{t.i}</div>
            <div style={{fontSize:8,fontWeight:600,color:t.a?'#6366F1':'#94A3B8'}}>{t.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchScreen() {
  return (
    <div style={{padding:'14px 12px 80px',background:'#0B0F1A',height:'100%',fontFamily:'Inter,sans-serif',overflowY:'auto',boxSizing:'border-box'}}>
      <div style={{fontSize:8,fontWeight:700,color:'#818CF8',letterSpacing:'0.14em',textTransform:'uppercase',marginBottom:4}}>Study groups</div>
      <div style={{fontSize:18,fontWeight:900,color:'#F1F5F9',letterSpacing:'-0.02em',marginBottom:12}}>My searches</div>
      <div style={{background:'rgba(52,211,153,0.1)',border:'1px solid rgba(52,211,153,0.25)',borderRadius:14,padding:12,marginBottom:8}}>
        <div style={{fontSize:8,fontWeight:800,color:'#6EE7B7',marginBottom:6}}>✓ MATCHED</div>
        <div style={{fontSize:13,fontWeight:800,color:'#F1F5F9',marginBottom:4}}>Spanish · A2</div>
        <div style={{fontSize:9,color:'#94A3B8',marginBottom:8}}>Sofia R. · Sat · 17:00</div>
        <div style={{height:28,borderRadius:8,background:'#34D399',color:'#0B0F1A',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>View event →</div>
      </div>
      <div style={{background:'#131826',border:'1px solid rgba(255,255,255,0.06)',borderLeft:'3px solid #818CF8',borderRadius:14,padding:12,marginBottom:8}}>
        <div style={{fontSize:8,fontWeight:800,color:'#C7D2FE',marginBottom:6}}>● SEARCHING</div>
        <div style={{fontSize:13,fontWeight:800,color:'#F1F5F9',marginBottom:2}}>English · B2</div>
        <div style={{fontSize:9,color:'#64748B'}}>Next check in 4:38</div>
      </div>
      <div style={{position:'absolute',bottom:0,left:0,right:0,height:64,background:'rgba(11,15,26,0.9)',backdropFilter:'blur(12px)',borderTop:'1px solid rgba(255,255,255,0.06)',display:'flex',justifyContent:'space-around',alignItems:'center',padding:'0 8px'}}>
        {[{i:'🔍',l:'Explore',a:false},{i:'💬',l:'Msg',a:false},{i:'👥',l:'Groups',a:true},{i:'👤',l:'Me',a:false}].map(t=>(
          <div key={t.l} style={{textAlign:'center'}}>
            <div style={{fontSize:18}}>{t.i}</div>
            <div style={{fontSize:8,fontWeight:600,color:t.a?'#818CF8':'#475569'}}>{t.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatScreen() {
  return (
    <div style={{background:'#0B0F1A',height:'100%',fontFamily:'Inter,sans-serif',display:'flex',flexDirection:'column'}}>
      <div style={{padding:'10px 12px',borderBottom:'1px solid rgba(255,255,255,0.06)',display:'flex',alignItems:'center',gap:8}}>
        <div style={{width:30,height:30,borderRadius:15,background:'linear-gradient(135deg,#818CF8,#A78BFA)',flexShrink:0,fontSize:10,fontWeight:700,color:'white',display:'flex',alignItems:'center',justifyContent:'center'}}>SI</div>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:'#F1F5F9'}}>Spanish Intermediate</div>
          <div style={{fontSize:9,color:'#10B981'}}>● 2 online</div>
        </div>
      </div>
      <div style={{flex:1,padding:'10px',overflowY:'auto',display:'flex',flexDirection:'column',gap:8}}>
        <div style={{textAlign:'center'}}><span style={{fontSize:9,color:'#64748B',background:'#1B2030',padding:'3px 10px',borderRadius:999}}>Today</span></div>
        <div style={{display:'flex',gap:6}}>
          <div style={{width:24,height:24,borderRadius:12,background:'linear-gradient(135deg,#818CF8,#A78BFA)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:700,color:'white'}}>S</div>
          <div>
            <div style={{fontSize:8,color:'#818CF8',marginBottom:2}}>Sofia</div>
            <div style={{background:'#1B2030',borderRadius:'12px 12px 12px 3px',padding:'7px 10px',fontSize:11,color:'#F1F5F9',maxWidth:160}}>¡Hola! ¿Todos listos? ☕</div>
          </div>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end'}}>
          <div style={{background:'linear-gradient(135deg,#818CF8,#A78BFA)',borderRadius:'12px 12px 3px 12px',padding:'7px 10px',fontSize:11,color:'white',maxWidth:160}}>Sí! Llegaré a las 11 🙌</div>
        </div>
      </div>
      <div style={{padding:'8px 10px',borderTop:'1px solid rgba(255,255,255,0.05)',display:'flex',gap:6,alignItems:'center'}}>
        <div style={{flex:1,height:32,borderRadius:16,background:'#1B2030',padding:'0 12px',display:'flex',alignItems:'center',fontSize:11,color:'#475569'}}>Message…</div>
        <div style={{width:32,height:32,borderRadius:16,background:'linear-gradient(135deg,#818CF8,#A78BFA)',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </div>
      </div>
    </div>
  );
}

// ── CheckIcon ───────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

// ── WaitlistLandingPage ─────────────────────────────────────────────────────

const API_BASE = '/api/getspeakhub-service/api/v1/public';

export const WaitlistLandingPage: React.FC = () => {
  const waitlistRef = useRef<HTMLElement>(null);

  // Countries / cities
  const [countries, setCountries]         = useState<CountryDto[]>([]);
  const [cities, setCities]               = useState<CityDto[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [useTextCity, setUseTextCity]     = useState(false);
  const [fallbackMode, setFallbackMode]   = useState(false);

  // Form
  const [form, setForm]   = useState<FormState>({ name:'', email:'', countryId:'', cityId:'', cityName:'', ambassador:false });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]       = useState<{ message: string; position: number } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load countries on mount
  useEffect(() => {
    fetch(`${API_BASE}/countries`)
      .then(r => r.json())
      .then((data: CountryDto[]) => setCountries(data.sort((a,b) => a.name.localeCompare(b.name))))
      .catch(() => {
        // fallback: build CountryDto list from static data
        const fallback = Object.keys(FALLBACK).sort().map((name, i) => ({ id: i+1, name, code: '' }));
        setCountries(fallback);
        setFallbackMode(true);
      });
  }, []);

  const handleCountryChange = useCallback(async (val: string) => {
    setForm(f => ({ ...f, countryId: val, cityId: '', cityName: '' }));
    setErrors(e => ({ ...e, country: undefined, city: undefined }));
    setUseTextCity(val === '__other');

    if (val === '__other' || val === '') { setCities([]); return; }

    if (fallbackMode) {
      const countryName = countries.find(c => String(c.id) === val)?.name ?? '';
      const cityNames = FALLBACK[countryName] ?? [];
      setCities(cityNames.map((name, i) => ({ id: i+1, name, country_id: Number(val) })));
      return;
    }

    setCitiesLoading(true);
    try {
      const r = await fetch(`${API_BASE}/cities?country_id=${val}`);
      const data: CityDto[] = await r.json();
      setCities(data.sort((a,b) => a.name.localeCompare(b.name)));
    } catch {
      const countryName = countries.find(c => String(c.id) === val)?.name ?? '';
      const cityNames = FALLBACK[countryName] ?? [];
      setCities(cityNames.map((name, i) => ({ id: i+1, name, country_id: Number(val) })));
    } finally {
      setCitiesLoading(false);
    }
  }, [countries, fallbackMode]);

  const handleCityChange = useCallback((val: string) => {
    setErrors(e => ({ ...e, city: undefined }));
    if (val === '__other') { setUseTextCity(true); setForm(f => ({ ...f, cityId: '', cityName: '' })); }
    else { setForm(f => ({ ...f, cityId: val, cityName: '' })); }
  }, []);

  const scrollToForm = useCallback(() => {
    waitlistRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => (document.getElementById('wl-name') as HTMLInputElement)?.focus(), 600);
  }, []);

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    if (!form.name.trim()) e.name = 'Please enter your first name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Please enter a valid email address.';
    if (!form.countryId) e.country = 'Please select your country.';
    if (useTextCity) { if (!form.cityName.trim()) e.city = 'Please enter your city.'; }
    else { if (!form.cityId) e.city = 'Please select your city.'; }
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const countryId = form.countryId === '__other' ? null : Number(form.countryId);
      const body: Record<string, unknown> = {
        name: form.name.trim(), email: form.email.trim(),
        country_id: countryId,
        ambassador: form.ambassador,
      };
      if (useTextCity) body.city_name = form.cityName.trim();
      else body.city_id = Number(form.cityId);

      const res = await fetch(`${API_BASE}/waitlist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });

      if (res.status === 201) {
        const data = await res.json();
        setSuccess({
          message: `Thanks, ${form.name.trim()}. We'll email you the moment GetSpeakHub opens in ${useTextCity ? form.cityName : cities.find(c=>String(c.id)===form.cityId)?.name ?? 'your city'}.`,
          position: data.position,
        });
      } else if (res.status === 409) {
        setSubmitError('This email is already on the waitlist.');
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="wl-root">
      {/* NAV */}
      <nav className="wl-nav">
        <div className="wl-nav-in">
          <a className="wl-logo" href="/">
            <div className="wl-logo-mark"><div className="wl-logo-inner"/></div>
            GetSpeakHub
          </a>
          <div>
            <button className="wl-btn-grad" onClick={scrollToForm}>Join the waitlist</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="wl-hero">
        <div className="wl-hero-glow-1"/>
        <div className="wl-hero-glow-2"/>
        <div className="wl-hero-in">
          <div>
            <div className="wl-eyebrow-badge">
              <span className="wl-blink"/>
              Coming 2026 — early access opening
            </div>
            <h1 className="wl-h1">
              Find Your<br/>Voice.<br/>Make Real<br/><span>Friends.</span>
            </h1>
            <p className="wl-hero-desc">
              More than just an exchange. GetSpeakHub brings people near you together to meet, speak, and connect through real-time meetups and meaningful conversations. We're putting the finishing touches on the app — join the waitlist to be among the first in your city.
            </p>
            <div className="wl-cta-row">
              <button className="wl-btn-lg primary" onClick={scrollToForm}>
                Join the waitlist
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
              <a className="wl-btn-lg ghost" href="#how">See how it works</a>
            </div>
            <div className="wl-social-row">
              <div className="wl-avatars">
                {[
                  ['SR','linear-gradient(135deg,#6366F1,#8B5CF6)'],
                  ['DK','linear-gradient(135deg,#10B981,#0EA5E9)'],
                  ['EA','linear-gradient(135deg,#F97316,#EF4444)'],
                  ['LT','linear-gradient(135deg,#8B5CF6,#EC4899)'],
                  ['MK','linear-gradient(135deg,#F59E0B,#F97316)'],
                ].map(([label, bg]) => (
                  <div key={label} className="wl-av" style={{ background: bg }}>{label}</div>
                ))}
              </div>
              <span className="wl-social-txt">Be among the <strong>first</strong> to get access in your city</span>
            </div>
          </div>

          {/* PHONES */}
          <div className="wl-phones">
            <div className="wl-phone left">
              <div className="wl-phone-inner"><MatchScreen/></div>
            </div>
            <div className="wl-phone main">
              <div className="wl-phone-inner"><ExploreScreen/></div>
            </div>
            <div className="wl-phone right">
              <div className="wl-phone-inner"><ChatScreen/></div>
            </div>

            <div className="wl-float-card wl-fc-match">
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:36,height:36,borderRadius:11,background:'linear-gradient(135deg,#10B981,#059669)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:'white'}}>Match found! 🎉</div>
                  <div style={{fontSize:11,color:'#94A3B8',marginTop:1}}>Spanish A2 · 3 others nearby</div>
                </div>
              </div>
            </div>

            <div className="wl-float-card wl-fc-stats">
              <div className="wl-fc-stat-val">4</div>
              <div style={{fontSize:11,fontWeight:600,color:'#94A3B8',marginTop:2}}>live sessions<br/>near you now</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAND */}
      <div className="wl-stats-band">
        <div className="wl-stats-in">
          {[
            {val:'50',lbl:'Languages at launch'},
            {val:'2026',lbl:'Launching soon'},
            {val:'Free',lbl:'For early-access users'},
            {val:'iOS · Android',lbl:'Both platforms'},
          ].map(s => (
            <div key={s.lbl}><div className="wl-stat-val">{s.val}</div><div className="wl-stat-lbl">{s.lbl}</div></div>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="wl-how" id="how">
        <div className="wl-how-in">
          <div className="wl-sc-header">
            <div className="wl-eyebrow">How it works</div>
            <h2 className="wl-section-h2">Up and running in 3 steps</h2>
          </div>
          <div className="wl-steps">
            {[
              {n:'1',h:'Set up your profile',p:'Create your profile in under 2 minutes. Pick your language, level (A1–C2), and radius. That\'s it.'},
              {n:'2',h:'We find your group',p:'Our engine runs every 10 minutes, matching by language, level, availability, and distance from you.'},
              {n:'3',h:'Meet & practise',p:'You get a push notification the moment we find a match. Open the app, say hi, pick a spot.'},
            ].map(s => (
              <div key={s.n}>
                <div className="wl-step-num">{s.n}</div>
                <h3 className="wl-step-h">{s.h}</h3>
                <p className="wl-step-p">{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="wl-features">
        <div className="wl-features-in">
          <div className="wl-sc-header">
            <div className="wl-eyebrow">Features</div>
            <h2 className="wl-section-h2">Built for people who<br/>want to actually speak</h2>
          </div>
          <div className="wl-feat-grid">
            {[
              {icon:'🎯',h:'Smart matching engine',p:'Radius + level + language + time window. Runs every 10 min. You get notified instantly.',hero:true},
              {icon:'📍',h:'Nearby sessions',p:'Browse live and upcoming meetups. Join a spontaneous table when someone\'s already there.'},
              {icon:'💬',h:'Group chat',p:'Each group gets its own chat room. Voice messages, image sharing, pinned meeting info.'},
              {icon:'⭐',h:'Reputation system',p:'Show up on time, contribute. Cancellations affect your score. Everyone\'s accountable.'},
              {icon:'🌙',h:'Dark mode',p:'Full dark mode across every screen. Looks great late-night reviewing session notes.'},
              {icon:'🔔',h:'Instant notifications',p:'Push notification the moment your match is ready. No polling, no refreshing.'},
            ].map(f => (
              <div key={f.h} className={`wl-feat-card${f.hero?' hero':''}`}>
                <div className="wl-feat-icon">{f.icon}</div>
                <h3 className="wl-feat-h">{f.h}</h3>
                <p className="wl-feat-p">{f.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LANGUAGES */}
      <section className="wl-langs-section">
        <div className="wl-langs-in">
          <div className="wl-eyebrow">Languages</div>
          <h2 className="wl-section-h2">Whatever you're learning,<br/>someone's practising it nearby</h2>
          <div className="wl-langs">
            {[
              {f:'🇬🇧',l:'English',a:true},{f:'🇪🇸',l:'Spanish'},{f:'🇫🇷',l:'French'},
              {f:'🇩🇪',l:'German'},{f:'🇯🇵',l:'Japanese'},{f:'🇰🇷',l:'Korean'},
              {f:'🇮🇹',l:'Italian'},{f:'🇵🇹',l:'Portuguese'},{f:'🇹🇷',l:'Turkish'},
              {f:'🇨🇳',l:'Mandarin'},{f:'🇷🇺',l:'Russian'},{f:'🇦🇪',l:'Arabic'},
            ].map(({ f, l, a }) => (
              <div key={l} className={`wl-lc${a?' active':''}`}><span>{f}</span>{l}</div>
            ))}
            <div className="wl-lc" style={{color:'#64748B',borderColor:'rgba(255,255,255,0.05)'}}>+ 40 more</div>
          </div>
        </div>
      </section>

      {/* WAITLIST FORM */}
      <section className="wl-form-section" id="waitlist" ref={waitlistRef}>
        <div className="wl-form-glow"/>
        <div className="wl-form-in">
          {/* Copy */}
          <div className="wl-copy">
            <div className="wl-eyebrow" style={{marginBottom:14}}>Early access</div>
            <h2 className="wl-copy-h">Reserve your spot.<br/><span>Find your match. Host local events.</span></h2>
            <p className="wl-copy-sub">GetSpeakHub is going global. Match with nearby friends to unlock your 'Official Region'. Connect locally and start creating real-life practice events together.</p>
            <div className="wl-bullets">
              {['Match instantly with language partners in your area','Create and join official local meetups','Get free early-access perks as a founding member'].map(b => (
                <div key={b} className="wl-bullet">
                  <span className="wl-check"><CheckIcon/></span>{b}
                </div>
              ))}
            </div>
          </div>

          {/* Card */}
          <div className="wl-card">
            {success ? (
              <div className="wl-success">
                <div className="wl-ws-badge">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="wl-ws-h">You're on the list! 🎉</div>
                <p className="wl-ws-p">{success.message}</p>
                <div className="wl-ws-pos">
                  <div className="wl-ws-pos-lbl">Your spot</div>
                  <div className="wl-ws-pos-val">#{success.position.toLocaleString()}</div>
                </div>
              </div>
            ) : (
              <>
                <div className="wl-card-title">Join the waitlist</div>
                <div className="wl-card-sub">Takes 20 seconds. No spam, ever.</div>

                {submitError && (
                  <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:10,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#FCA5A5'}}>
                    {submitError}
                  </div>
                )}

                <form className="wl-form-grid" onSubmit={handleSubmit} noValidate>
                  <div className={`wl-field full${errors.name ? ' error' : ''}`}>
                    <label htmlFor="wl-name">First name <span className="wl-req">*</span></label>
                    <input id="wl-name" className="wl-inp" type="text" placeholder="e.g. Alex" autoComplete="given-name"
                      value={form.name} onChange={e => { setForm(f=>({...f,name:e.target.value})); setErrors(er=>({...er,name:undefined})); }}/>
                    {errors.name && <span className="wl-err">{errors.name}</span>}
                  </div>

                  <div className={`wl-field full${errors.email ? ' error' : ''}`}>
                    <label htmlFor="wl-email">Email address <span className="wl-req">*</span></label>
                    <input id="wl-email" className="wl-inp" type="email" placeholder="you@example.com" autoComplete="email"
                      value={form.email} onChange={e => { setForm(f=>({...f,email:e.target.value})); setErrors(er=>({...er,email:undefined})); }}/>
                    {errors.email && <span className="wl-err">{errors.email}</span>}
                  </div>

                  <div className={`wl-field${errors.country ? ' error' : ''}`}>
                    <label htmlFor="wl-country">Country <span className="wl-req">*</span></label>
                    <select id="wl-country" className={`wl-sel${!form.countryId ? ' placeholder' : ''}`}
                      value={form.countryId}
                      onChange={e => handleCountryChange(e.target.value)}>
                      <option value="" disabled>Select country</option>
                      {countries.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                      <option value="__other">Other / Not listed</option>
                    </select>
                    {errors.country && <span className="wl-err">{errors.country}</span>}
                  </div>

                  <div className={`wl-field${errors.city ? ' error' : ''}`}>
                    <label htmlFor="wl-city">City <span className="wl-req">*</span></label>
                    {useTextCity ? (
                      <input id="wl-city" className="wl-inp" type="text" placeholder="Type your city"
                        value={form.cityName}
                        onChange={e => { setForm(f=>({...f,cityName:e.target.value})); setErrors(er=>({...er,city:undefined})); }}/>
                    ) : (
                      <select id="wl-city" className={`wl-sel${!form.cityId ? ' placeholder' : ''}`}
                        value={form.cityId} disabled={!form.countryId || citiesLoading}
                        onChange={e => handleCityChange(e.target.value)}>
                        <option value="" disabled>{!form.countryId ? 'Select country first' : citiesLoading ? 'Loading…' : 'Select city'}</option>
                        {cities.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                        {cities.length > 0 && <option value="__other">Other / Not listed</option>}
                      </select>
                    )}
                    {errors.city && <span className="wl-err">{errors.city}</span>}
                  </div>

                  <div className="wl-field full">
                    <label>Do you want to be an ambassador?</label>
                    <div className="wl-amb-row" role="radiogroup" aria-label="Do you want to be an ambassador?">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={form.ambassador}
                        className={`wl-amb-btn${form.ambassador ? ' active' : ''}`}
                        onClick={() => setForm(f => ({ ...f, ambassador: true }))}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={!form.ambassador}
                        className={`wl-amb-btn${!form.ambassador ? ' active' : ''}`}
                        onClick={() => setForm(f => ({ ...f, ambassador: false }))}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  <button className="wl-submit" type="submit" disabled={submitting}>
                    {submitting ? 'Joining…' : 'Join the waitlist'}
                    {!submitting && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>}
                  </button>
                </form>

                <p className="wl-note">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign:'-2px',marginRight:3}}><rect width="18" height="11" x="3" y="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  We'll only email you about launch news. Unsubscribe anytime.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="wl-footer">
        <div className="wl-footer-in">
          <div className="wl-logo">
            <div className="wl-logo-mark" style={{width:28,height:28,borderRadius:8}}>
              <div className="wl-logo-inner" style={{width:11,height:11}}/>
            </div>
            GetSpeakHub
          </div>
          <div className="wl-foot-links">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <a href="mailto:hello@getspeakhub.com">Contact</a>
          </div>
          <div className="wl-foot-copy">© 2026 GetSpeakHub. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default WaitlistLandingPage;
